/**
 * Klick-zum-Hinzufügen aus der Library in einen Channel.
 *
 * Spiegelt den Library→Channel-Drag (useImageDragDrop: isFromLibrary-Zweig):
 * erzeugt einen `pendingUpload` für den Ziel-Channel und markiert ihn als
 * geändert — KEINE sofortige Persistierung (Channel-Bilder-Spec), Speichern
 * läuft über die bestehende Save-Bar.
 */

import { toast } from 'sonner';
import type { ProductImageAsset } from '@/types/image-collection';
import type { PendingUpload, PendingCopy } from './types';

interface AddDeps {
  groupedByChannel: Record<string, { asset_id: string }[]>;
  channelMeta: Record<string, { channel_uuid?: string }>;
  pendingUploads: PendingUpload[];
  pendingCopies: PendingCopy[];
  setPendingUploads: React.Dispatch<React.SetStateAction<PendingUpload[]>>;
  setChangedChannelKeys: (fn: (prev: Set<string>) => Set<string>) => void;
  channelName: string;
}

export function addLibraryAssetToChannel(asset: ProductImageAsset, channelId: string, deps: AddDeps): void {
  // Global wird als Key 'global' geführt, sonst ist der Key = Channel-UUID.
  const globalUuid = deps.channelMeta['global']?.channel_uuid;
  const channelKey = channelId === globalUuid ? 'global' : channelId;

  const alreadyInChannel = (deps.groupedByChannel[channelKey] || []).some((a) => a.asset_id === asset.asset_id);
  const alreadyPendingUpload = deps.pendingUploads.some((p) => p.assetId === asset.asset_id && p.channelKey === channelKey);
  const alreadyPendingCopy = deps.pendingCopies.some((p) => p.sourceAssetId === asset.asset_id && p.targetChannelKey === channelKey);

  if (alreadyInChannel || alreadyPendingUpload || alreadyPendingCopy) {
    toast.warning(`Bild ist bereits in „${deps.channelName}"`);
    return;
  }

  deps.setPendingUploads((prev) => [
    ...prev,
    { assetId: asset.asset_id, channelId, channelKey, width: asset.width || 0, height: asset.height || 0 },
  ]);
  deps.setChangedChannelKeys((prev) => new Set(prev).add(channelKey));
  toast.info(`Bild zu „${deps.channelName}" hinzugefügt – Speichern zum Übernehmen`);
}
